import { useEffect, useMemo, useState } from "react";
import { LeadAPI } from "api/lead/service";
import {
  Check,
  Send,
  ChevronDown,
  ChevronRight,
  Clock,
  MessageSquare,
  Edit3,
  Image as ImageIcon,
  Trash2,
  Plus,
  MoreVertical,
  Pencil,
  ArrowRight,
  Eye,
  X,
  ChevronLeft,
  RefreshCw,
} from "lucide-react";

function formatDateOnly(isoLike) {
  if (!isoLike) return "";
  return String(isoLike).split("T")[0];
}

const DEFAULT_REQUIREMENT_COLOR = "#3B0069";

function normalizeBooleanValue(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw === "yes" || raw === "true") return "Yes";
  if (raw === "no" || raw === "false") return "No";
  return "";
}

function normalizeColorValue(value) {
  const raw = String(value || "").trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw)) return raw;
  return DEFAULT_REQUIREMENT_COLOR;
}

function parseDimensionValue(value) {
  const raw = String(value || "").trim();
  if (!raw) return { a: "", b: "", c: "", is3d: false };
  const parts = raw.split(/[xX]/).map((p) => p.trim());
  return {
    a: parts[0] || "",
    b: parts[1] || "",
    c: parts[2] || "",
    is3d: parts.length > 2,
  };
}

function buildDimensionValue(a, b, c, is3d) {
  const cleanedA = String(a || "").trim();
  const cleanedB = String(b || "").trim();
  const cleanedC = String(c || "").trim();
  if (is3d) return `${cleanedA}x${cleanedB}x${cleanedC}`;
  if (!cleanedA && !cleanedB) return "";
  return `${cleanedA}x${cleanedB}`;
}

export function ScopeOfWorkView({
  client,
  onCreateQuotation,
  canApprove = false,
  canViewQuotation = true,
}) {
  const [scope, setScope] = useState(null);
  const [isLoadingScope, setIsLoadingScope] = useState(false);
  const [scopeError, setScopeError] = useState("");
  const [scopeActionError, setScopeActionError] = useState("");
  const [isWorkingOnScopeAction, setIsWorkingOnScopeAction] = useState(false);
  const [isProceedingToQuotation, setIsProceedingToQuotation] = useState(false);
  const [isClientIssueFlow, setIsClientIssueFlow] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [scopeVersions, setScopeVersions] = useState([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [versionsError, setVersionsError] = useState("");
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [versionDetail, setVersionDetail] = useState(null);
  const [isLoadingVersionDetail, setIsLoadingVersionDetail] = useState(false);
  const [versionDetailError, setVersionDetailError] = useState("");
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategories, setNewCategories] = useState([{ name: "" }]);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [categoryError, setCategoryError] = useState("");

  const [addingRequirementsFor, setAddingRequirementsFor] = useState(null);
  // const [newRequirements, setNewRequirements] = useState([{ field: "", type: "Text", value: "", imageFiles: [] }]);
  const [newRequirements, setNewRequirements] = useState([
    { field: "", type: "", value: "", imageFiles: [] },
  ]);
  const [isCreatingRequirement, setIsCreatingRequirement] = useState(false);
  const [requirementError, setRequirementError] = useState("");
  const [feedback, setFeedback] = useState([]);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [newFeedbackMessage, setNewFeedbackMessage] = useState("");
  const [isCreatingFeedback, setIsCreatingFeedback] = useState(false);
  const [feedbackCategory, setFeedbackCategory] = useState("approver");
  const [isRejectScopeFlow, setIsRejectScopeFlow] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // { type: "category"|"feedback", id: string }
  const [quotationExists, setQuotationExists] = useState(false);
  const [editCategoryModal, setEditCategoryModal] = useState(null); // { id, name }
  const [editCategoryName, setEditCategoryName] = useState("");
  const [isSavingCategory, setIsSavingCategory] = useState(false);
  const [editRequirementModal, setEditRequirementModal] = useState(null); // { id, categoryId, field, type, value }
  const [editRequirement, setEditRequirement] = useState({
    field: "",
    type: "",
    value: "",
  });
  const [isSavingRequirement, setIsSavingRequirement] = useState(false);
  const [editFeedbackModal, setEditFeedbackModal] = useState(null); // { id, message }
  const [editFeedbackMessage, setEditFeedbackMessage] = useState("");
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);
  const [isRefreshingView, setIsRefreshingView] = useState(false);

  // New state for Image Carousel
  const [imageViewerState, setImageViewerState] = useState({
    isOpen: false,
    images: [],
    currentIndex: 0,
  });

  // New state for adding images in Edit Requirement
  const [editRequirementNewFiles, setEditRequirementNewFiles] = useState([]);
  const [confirmDeleteReqImage, setConfirmDeleteReqImage] = useState(null); // { requirementId, imageId }
  const [isDeletingReqImage, setIsDeletingReqImage] = useState(false);
  const [deleteReqImageError, setDeleteReqImageError] = useState("");

  function normalizeScope(raw) {
    if (!raw) return null;
    const payload =
      raw?.data && typeof raw?.success !== "undefined" ? raw.data : raw;
    const statusRaw = String(payload.status || "")
      .trim()
      .toLowerCase();
    const status =
      statusRaw === "draft"
        ? "Draft"
        : statusRaw === "sent"
          ? "Sent"
          : statusRaw === "approver_approved"
            ? "Approver Approved"
            : statusRaw === "client_approved"
              ? "Client Approved"
              : statusRaw === "approved"
                ? "Approved"
                : statusRaw === "rejected"
                  ? "Rejected"
                  : payload.status || "Draft";

    const tentativeStart =
      payload.tentative_start ||
      payload.tentativeStart ||
      payload.start_date ||
      payload.startDate ||
      "";

    return {
      id: String(payload.id ?? payload.pk ?? ""),
      name: payload.name || "Scope of Work",
      projectType:
        payload.project_type ||
        payload.projectType ||
        client?.projectType ||
        "Interior",
      propertySize:
        payload.property_size ||
        payload.propertySize ||
        client?.propertySize ||
        "",
      budget: payload.budget ?? "",
      tentativeStart: tentativeStart
        ? String(tentativeStart).split("T")[0]
        : "",
      status,
      statusRaw,
      categories: (payload.categories || []).map((c) => ({
        ...c,
        requirements: Array.isArray(c.requirements) ? c.requirements : [],
      })),
      versions: payload.versions || [],
      currentVersion: payload.current_version ?? payload.currentVersion,
    };
  }

  const refreshScope = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    const res = await LeadAPI.getScopeFull(orgId, leadId);
    const mapped = normalizeScope(res);
    setScope(mapped);
    return mapped;
  };

  const refreshQuotationExists = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    try {
      // Using the same endpoint as QuotationView to avoid false negatives.
      await LeadAPI.getQuotationFull(orgId, leadId);
      setQuotationExists(true);
    } catch (e) {
      // Only confidently mark as not-existing on 404.
      if (e?.status === 404) setQuotationExists(false);
    }
  };

  const refreshScopeFeedback = async (scopeIdOverride) => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    const scopeId = scopeIdOverride || scope?.id;
    if (!orgId || !leadId || !scopeId) return;
    const res = await LeadAPI.listScopeFeedback(orgId, leadId, scopeId);
    const list = Array.isArray(res) ? res : (res?.data || res?.results || []);
    setFeedback(list || []);
  };

  const handleRefreshView = async () => {
    if (isRefreshingView) return;
    setIsRefreshingView(true);
    setScopeActionError("");
    try {
      const mapped = await refreshScope();
      const currentScopeId = mapped?.id || scope?.id;
      const requests = [refreshQuotationExists(), refreshScopeFeedback(currentScopeId)];
      if (showVersionHistory) requests.push(loadVersions());
      if (isVersionModalOpen && selectedVersionId) requests.push(loadVersionDetail(selectedVersionId));
      await Promise.all(requests);
    } catch (e) {
      setScopeActionError(e?.message || "Failed to refresh scope");
    } finally {
      setIsRefreshingView(false);
    }
  };

  const normalizedFeedback = useMemo(() => {
    const toCategoryLabel = (category) => {
      const normalized = String(category || "").trim().toLowerCase();
      if (!normalized) return "";
      if (normalized === "client") return "Client";
      if (normalized === "approver") return "Approver";
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    };

    const list = Array.isArray(feedback) ? feedback : [];
    return list
      .map((f) => ({
        id: String(f?.id ?? f?.pk ?? ""),
        message: f?.message || f?.text || "",
        date: formatDateOnly(
          f?.created_on || f?.createdAt || f?.created_at || f?.date || "",
        ),
        by: f?.created_by || f?.updated_by || "",
        isClient:
          typeof f?.is_client === "boolean"
            ? f.is_client
            : f?.by
              ? String(f.by).toLowerCase().includes("client")
              : false,
        category: String(f?.category || "").trim().toLowerCase(),
        categoryLabel: toCategoryLabel(f?.category),
      }))
      .filter((f) => f.id || f.message);
  }, [feedback]);

  const closeFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
    setNewFeedbackMessage("");
    setIsClientIssueFlow(false);
    setIsRejectScopeFlow(false);
    setFeedbackCategory("approver");
  };

  const versionSnapshotCategories = useMemo(() => {
    const snapshot =
      versionDetail?.snapshot_data || versionDetail?.snapshot || {};
    const cats = Array.isArray(snapshot?.categories) ? snapshot.categories : [];
    return cats.map((category) => ({
      ...category,
      requirements: Array.isArray(category?.requirements)
        ? category.requirements
        : [],
    }));
  }, [versionDetail]);

  useEffect(() => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;

    let cancelled = false;
    (async () => {
      setScopeError("");
      setIsLoadingScope(true);
      try {
        const res = await LeadAPI.getScopeFull(orgId, leadId);
        const mapped = normalizeScope(res);
        if (!cancelled) setScope(mapped);

        // Also check if quotation exists
        try {
          await LeadAPI.getQuotationFull(orgId, leadId);
          if (!cancelled) setQuotationExists(true);
        } catch (e) {
          if (!cancelled && e?.status === 404) setQuotationExists(false);
        }
      } catch (e) {
        // If scope doesn't exist yet, show empty state
        if (!cancelled) {
          const status = e?.status;
          if (status === 404) setScope(null);
          else {
            setScope(null);
            setScopeError(e?.message || "Failed to load scope");
          }
        }
      } finally {
        if (!cancelled) setIsLoadingScope(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [client?.id]);

  // If the user created a quotation in another view and navigates back here without unmounting,
  // `quotationExists` can become stale. Refresh it when the tab/window regains focus.
  useEffect(() => {
    if (!client?.id) return;

    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      await refreshQuotationExists();
    };

    const onFocus = () => run();
    const onVisibility = () => {
      if (document.visibilityState === "visible") run();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id]);

  useEffect(() => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    const scopeId = scope?.id;
    if (!orgId || !leadId || !scopeId) return;

    let cancelled = false;
    (async () => {
      setFeedbackError("");
      setIsLoadingFeedback(true);
      try {
        const res = await LeadAPI.listScopeFeedback(orgId, leadId, scopeId);
        const list = Array.isArray(res) ? res : res?.data || res?.results || [];
        if (!cancelled) setFeedback(list || []);
      } catch (e) {
        if (!cancelled)
          setFeedbackError(e?.message || "Failed to load client feedback");
      } finally {
        if (!cancelled) setIsLoadingFeedback(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [client?.id, scope?.id]);

  const handleCreateFeedback = async () => {
    const currentStatusRaw = String(scope?.statusRaw || "").trim().toLowerCase();
    if (currentStatusRaw === "client_approved" || currentStatusRaw === "client approved") return;
    const message = String(newFeedbackMessage || "").trim();
    if (!message) return;

    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    const scopeId = scope?.id;
    if (!orgId || !leadId || !scopeId) {
      setFeedbackError("Organization/Lead/Scope not found");
      return;
    }

    setIsCreatingFeedback(true);
    setFeedbackError("");
    try {
      await LeadAPI.createScopeFeedback(orgId, leadId, scopeId, {
        scope: Number(scopeId),
        message,
        category: feedbackCategory,
      });
      if (isClientIssueFlow) {
        await LeadAPI.reopenScope(orgId, leadId);
        await refreshScope();
      }
      if (isRejectScopeFlow) {
        await LeadAPI.rejectScopeAsApprover(orgId, leadId);
        await refreshScope();
      }
      closeFeedbackModal();

      // Re-fetch to ensure refresh correctness
      const res = await LeadAPI.listScopeFeedback(orgId, leadId, scopeId);
      const list = Array.isArray(res) ? res : res?.data || res?.results || [];
      setFeedback(list || []);
    } catch (e) {
      setFeedbackError(e?.message || "Failed to save feedback");
    } finally {
      setIsCreatingFeedback(false);
    }
  };

  const openCategoryEdit = (cat) => {
    setCategoryError("");
    setEditCategoryModal({ id: String(cat.id), name: cat.name || "" });
    setEditCategoryName(cat.name || "");
  };

  const saveCategoryEdit = async () => {
    const name = String(editCategoryName || "").trim();
    if (!name || !editCategoryModal) return;
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    setIsSavingCategory(true);
    setCategoryError("");
    try {
      const categoryId = editCategoryModal.id;
      setScope((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          categories: (prev.categories || []).map((c) =>
            String(c.id) === String(categoryId) ? { ...c, name } : c,
          ),
        };
      });
      await LeadAPI.updateScopeCategory(orgId, leadId, categoryId, { name });
      await refreshScope();
      setEditCategoryModal(null);
    } catch (e) {
      setCategoryError(e?.message || "Failed to update category");
      await refreshScope().catch(() => { });
    } finally {
      setIsSavingCategory(false);
    }
  };

  const deleteCategory = async (categoryId) => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    try {
      setScope((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          categories: (prev.categories || []).filter(
            (c) => String(c.id) !== String(categoryId),
          ),
        };
      });
      await LeadAPI.deleteScopeCategory(orgId, leadId, categoryId);
      await refreshScope();
    } catch (_) {
      await refreshScope().catch(() => { });
    }
  };

  const openRequirementEdit = (categoryId, req) => {
    setRequirementError("");
    setEditRequirementModal({
      id: String(req.id),
      categoryId: String(categoryId),
      field: req.field || "",
      type: req.type || "",
      value: req.value || "",
      existingImages: Array.isArray(req.images) ? req.images : [],
    });
    setEditRequirement({
      field: req.field || "",
      type: req.type || "",
      value: req.value || "",
    });
    setEditRequirementNewFiles([]);
    setConfirmDeleteReqImage(null);
    setIsDeletingReqImage(false);
    setDeleteReqImageError("");
  };

  const getRequirementImageId = (img) => {
    const id = img?.id ?? img?.pk ?? img?.image_id ?? img?.imageId;
    return id != null && id !== "" ? id : null;
  };

  const confirmDeleteExistingRequirementImage = (img) => {
    if (!editRequirementModal) return;
    const imageId = getRequirementImageId(img);
    setDeleteReqImageError("");
    if (imageId == null) {
      setDeleteReqImageError("Image id not found");
    }
    setConfirmDeleteReqImage({ requirementId: editRequirementModal.id, imageId });
  };

  const handleDeleteExistingRequirementImage = async () => {
    if (!confirmDeleteReqImage) return;
    if (confirmDeleteReqImage.imageId == null) {
      setDeleteReqImageError("Image id not found");
      return;
    }
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;

    setIsDeletingReqImage(true);
    setDeleteReqImageError("");
    try {
      await LeadAPI.deleteRequirementImage(
        orgId,
        leadId,
        confirmDeleteReqImage.requirementId,
        confirmDeleteReqImage.imageId
      );

      const shouldKeepImage = (img) => String(getRequirementImageId(img)) !== String(confirmDeleteReqImage.imageId);

      // Update modal images immediately
      setEditRequirementModal((prev) => {
        if (!prev) return prev;
        return { ...prev, existingImages: (prev.existingImages || []).filter(shouldKeepImage) };
      });

      // Update the main scope state too (so list view matches without refresh)
      setScope((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          categories: (prev.categories || []).map((c) => ({
            ...c,
            requirements: (c.requirements || []).map((r) => {
              if (String(r.id) !== String(confirmDeleteReqImage.requirementId)) return r;
              const nextImages = (Array.isArray(r.images) ? r.images : []).filter(shouldKeepImage);
              return { ...r, images: nextImages };
            }),
          })),
        };
      });

      // If the carousel is open on this list, keep it consistent as well.
      setImageViewerState((prev) => {
        if (!prev?.isOpen) return prev;
        const nextImages = (prev.images || []).filter(shouldKeepImage);
        const nextIndex = Math.min(prev.currentIndex, Math.max(0, nextImages.length - 1));
        return { ...prev, images: nextImages, currentIndex: nextIndex, isOpen: nextImages.length > 0 };
      });

      setConfirmDeleteReqImage(null);
      await refreshScope().catch(() => { });
    } catch (e) {
      setDeleteReqImageError(e?.message || "Failed to delete image");
    } finally {
      setIsDeletingReqImage(false);
    }
  };

  const saveRequirementEdit = async () => {
    if (!editRequirementModal) return;
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;

    const payload = {
      field: String(editRequirement.field || "").trim(),
      type: String(editRequirement.type || "").trim(),
      value: String(editRequirement.value || "").trim(),
    };
    if (!payload.field || !payload.type) return;

    setIsSavingRequirement(true);
    setRequirementError("");
    try {
      const reqId = editRequirementModal.id;
      setScope((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          categories: (prev.categories || []).map((c) => ({
            ...c,
            requirements: (c.requirements || []).map((r) =>
              String(r.id) === String(reqId) ? { ...r, ...payload } : r,
            ),
          })),
        };
      });
      await LeadAPI.updateRequirement(orgId, leadId, reqId, payload);

      if (editRequirementNewFiles.length > 0) {
        for (const f of editRequirementNewFiles) {
          await LeadAPI.uploadRequirementImage(orgId, leadId, reqId, f);
        }
      }

      await refreshScope();
      setEditRequirementModal(null);
      setConfirmDeleteReqImage(null);
      setDeleteReqImageError("");
    } catch (e) {
      setRequirementError(e?.message || "Failed to update requirement");
      await refreshScope().catch(() => { });
    } finally {
      setIsSavingRequirement(false);
    }
  };

  const deleteRequirement = async (reqId) => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    try {
      setScope((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          categories: (prev.categories || []).map((c) => ({
            ...c,
            requirements: (c.requirements || []).filter(
              (r) => String(r.id) !== String(reqId),
            ),
          })),
        };
      });
      await LeadAPI.deleteRequirement(orgId, leadId, reqId);
      await refreshScope();
    } catch (_) {
      await refreshScope().catch(() => { });
    }
  };

  const openRequirementImage = (req) => {
    const list = Array.isArray(req.images) ? req.images : [];
    if (list.length === 0) return;
    setImageViewerState({
      isOpen: true,
      images: list,
      currentIndex: 0,
    });
  };

  const openFeedbackEdit = (fb) => {
    const currentStatusRaw = String(scope?.statusRaw || "").trim().toLowerCase();
    if (currentStatusRaw === "client_approved" || currentStatusRaw === "client approved") return;
    setFeedbackError("");
    setEditFeedbackModal({ id: String(fb.id), message: fb.message || "" });
    setEditFeedbackMessage(fb.message || "");
  };

  const saveFeedbackEdit = async () => {
    const currentStatusRaw = String(scope?.statusRaw || "").trim().toLowerCase();
    if (currentStatusRaw === "client_approved" || currentStatusRaw === "client approved") return;
    const message = String(editFeedbackMessage || "").trim();
    if (!message || !editFeedbackModal) return;
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    const scopeId = scope?.id;
    if (!orgId || !leadId || !scopeId) return;
    setIsSavingFeedback(true);
    setFeedbackError("");
    try {
      const id = editFeedbackModal.id;
      setFeedback((prev) =>
        (Array.isArray(prev) ? prev : []).map((x) =>
          String(x.id) === String(id) ? { ...x, message } : x,
        ),
      );
      await LeadAPI.updateScopeFeedback(orgId, leadId, scopeId, id, {
        message,
      });
      const res = await LeadAPI.listScopeFeedback(orgId, leadId, scopeId);
      const list = Array.isArray(res) ? res : res?.data || res?.results || [];
      setFeedback(list || []);
      setEditFeedbackModal(null);
    } catch (e) {
      setFeedbackError(e?.message || "Failed to update feedback");
    } finally {
      setIsSavingFeedback(false);
    }
  };

  const deleteFeedback = async (fbId) => {
    const currentStatusRaw = String(scope?.statusRaw || "").trim().toLowerCase();
    if (currentStatusRaw === "client_approved" || currentStatusRaw === "client approved") return;
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    const scopeId = scope?.id;
    if (!orgId || !leadId || !scopeId) return;
    try {
      setFeedback((prev) =>
        (Array.isArray(prev) ? prev : []).filter(
          (x) => String(x.id) !== String(fbId),
        ),
      );
      await LeadAPI.deleteScopeFeedback(orgId, leadId, scopeId, fbId);
      const res = await LeadAPI.listScopeFeedback(orgId, leadId, scopeId);
      const list = Array.isArray(res) ? res : res?.data || res?.results || [];
      setFeedback(list || []);
    } catch (_) {
      const res = await LeadAPI.listScopeFeedback(orgId, leadId, scopeId);
      const list = Array.isArray(res) ? res : res?.data || res?.results || [];
      setFeedback(list || []);
    }
  };

  const handleCreateRequirements = async (categoryId) => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId || !categoryId) return;

    const validReqs = newRequirements.filter((r) => r.field.trim() && r.type);
    if (validReqs.length === 0) return;

    setIsCreatingRequirement(true);
    setRequirementError("");
    try {
      for (const r of validReqs) {
        const res = await LeadAPI.createRequirement(orgId, leadId, categoryId, {
          field: r.field.trim(),
          type: r.type,
          value: r.value.trim(),
        });
        const created = res?.data || res;
        const requirementId = created?.id ?? created?.pk;

        if (r.imageFiles?.length > 0) {
          for (const f of r.imageFiles) {
            await LeadAPI.uploadRequirementImage(
              orgId,
              leadId,
              requirementId,
              f,
            );
          }
        }
      }
      await refreshScope();
      setAddingRequirementsFor(null);
      setNewRequirements([
        { field: "", type: "Text", value: "", imageFiles: [] },
      ]);
    } catch (e) {
      setRequirementError(e?.message || "Failed to add requirements");
    } finally {
      setIsCreatingRequirement(false);
    }
  };

  const handleCreateCategories = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;

    const validCats = newCategories.filter((c) => c.name.trim());
    if (validCats.length === 0) return;

    setIsCreatingCategory(true);
    setCategoryError("");
    try {
      for (const c of validCats) {
        await LeadAPI.createScopeCategory(orgId, leadId, {
          name: c.name.trim(),
        });
      }
      await refreshScope();
      setIsAddingCategory(false);
      setNewCategories([{ name: "" }]);
    } catch (e) {
      setCategoryError(e?.message || "Failed to add categories");
    } finally {
      setIsCreatingCategory(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      Draft: "bg-gray-200 text-gray-700",
      Sent: "bg-blue-100 text-blue-700",
      "Sent for Approval": "bg-blue-100 text-blue-700",
      Open: "bg-gray-200 text-gray-700",
      "Approver Approved": "bg-orange-100 text-orange-700",
      "Client Approved": "bg-green-100 text-green-700",
      Approved: "bg-green-100 text-green-700",
      Rejected: "bg-red-100 text-red-700",
    };
    return (
      <span
        className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${styles[status]}`}
      >
        {status}
      </span>
    );
  };

  const statusRaw = String(scope?.statusRaw || "").toLowerCase();
  const isLockedForEdits = [
    "sent",
    "approver_approved",
    "client_approved",
  ].includes(statusRaw);
  const isClientApproved = statusRaw === "client_approved";
  const canEditRequirements = !isLockedForEdits;

  const statusTagForHeader = () => {
    if (canApprove) {
      if (statusRaw === "draft") return "Open";
      if (statusRaw === "sent") return "Sent for Approval";
      if (statusRaw === "approver_approved") return "Approver Approved";
      if (statusRaw === "client_approved") return "Client Approved";
      return scope?.status || "Open";
    }
    // non-approver
    if (statusRaw === "sent") return "Sent for Approval";
    if (statusRaw === "approver_approved") return "Approver Approved";
    if (statusRaw === "client_approved") return "Client Approved";
    return scope?.status || "Draft";
  };

  const sendForApproval = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    setScopeActionError("");
    setIsWorkingOnScopeAction(true);
    try {
      await LeadAPI.sendScopeForApproval(orgId, leadId);
      await refreshScope();
    } catch (e) {
      setScopeActionError(e?.message || "Failed to send for approval");
    } finally {
      setIsWorkingOnScopeAction(false);
    }
  };

  const selfApproveAndSendToClient = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    setScopeActionError("");
    setIsWorkingOnScopeAction(true);
    try {
      await LeadAPI.updateScope(orgId, leadId, { status: "approver_approved" });
      await refreshScope();
    } catch (e) {
      setScopeActionError(e?.message || "Failed to approve scope");
    } finally {
      setIsWorkingOnScopeAction(false);
    }
  };

  const approveAsApprover = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    setScopeActionError("");
    setIsWorkingOnScopeAction(true);
    try {
      await LeadAPI.approveScopeAsApprover(orgId, leadId);
      await refreshScope();
    } catch (e) {
      setScopeActionError(e?.message || "Failed to approve scope");
    } finally {
      setIsWorkingOnScopeAction(false);
    }
  };

  const rejectAsApprover = async () => {
    setFeedbackError("");
    setNewFeedbackMessage("");
    setIsClientIssueFlow(false);
    setIsRejectScopeFlow(true);
    setFeedbackCategory("client");
    setIsFeedbackModalOpen(true);
  };

  const approveAsClient = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    setScopeActionError("");
    setIsWorkingOnScopeAction(true);
    try {
      await LeadAPI.approveScopeAsClient(orgId, leadId);
      await refreshScope();
    } catch (e) {
      setScopeActionError(e?.message || "Failed to mark client approved");
    } finally {
      setIsWorkingOnScopeAction(false);
    }
  };

  const openClientIssueModal = () => {
    setFeedbackError("");
    setNewFeedbackMessage("");
    setIsClientIssueFlow(true);
    setIsRejectScopeFlow(false);
    setFeedbackCategory("approver");
    setIsFeedbackModalOpen(true);
  };

  const proceedToQuotation = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId || !scope?.id) return;
    setScopeActionError("");
    setIsProceedingToQuotation(true);
    try {
      // If quotation exists, just navigate
      try {
        await LeadAPI.getQuotationFull(orgId, leadId);
      } catch (e) {
        if (e?.status === 404) {
          await LeadAPI.createQuotation(orgId, leadId, {
            scope: Number(scope.id),
            name: `Quotation for ${scope.name}`,
            payment_terms: "",
            terms_conditions: "",
          });
        } else {
          throw e;
        }
      }
      setQuotationExists(true);
      onCreateQuotation(scope.id);
    } catch (e) {
      setScopeActionError(e?.message || "Failed to proceed to quotation");
    } finally {
      setIsProceedingToQuotation(false);
    }
  };

  const loadVersions = async () => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId) return;
    setVersionsError("");
    setIsLoadingVersions(true);
    try {
      const res = await LeadAPI.listScopeVersions(orgId, leadId);
      const list = Array.isArray(res) ? res : res?.data || res?.results || [];
      setScopeVersions(Array.isArray(list) ? list : []);
    } catch (e) {
      setVersionsError(e?.message || "Failed to load version history");
    } finally {
      setIsLoadingVersions(false);
    }
  };

  const loadVersionDetail = async (versionId) => {
    const orgId = LeadAPI.orgId();
    const leadId = client?.id;
    if (!orgId || !leadId || !versionId) return;
    setVersionDetailError("");
    setIsLoadingVersionDetail(true);
    try {
      const res = await LeadAPI.getScopeVersion(orgId, leadId, versionId);
      const data =
        res?.data && typeof res?.success !== "undefined" ? res.data : res;
      setVersionDetail(data);
    } catch (e) {
      setVersionDetailError(e?.message || "Failed to load version detail");
    } finally {
      setIsLoadingVersionDetail(false);
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem(
      `scopeVersionId_${client?.id || "none"}`,
    );
    if (saved) {
      const parsed = Number(saved);
      if (!Number.isNaN(parsed)) {
        setSelectedVersionId(parsed);
        loadVersionDetail(parsed);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id]);

  useEffect(() => {
    if (selectedVersionId) {
      localStorage.setItem(
        `scopeVersionId_${client?.id || "none"}`,
        String(selectedVersionId),
      );
    } else {
      localStorage.removeItem(`scopeVersionId_${client?.id || "none"}`);
    }
  }, [selectedVersionId, client?.id]);

  // Effect for Keyboard Navigation in Image Carousel
  useEffect(() => {
    if (!imageViewerState.isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        setImageViewerState((prev) => ({
          ...prev,
          currentIndex:
            prev.currentIndex === 0
              ? prev.images.length - 1
              : prev.currentIndex - 1,
        }));
      } else if (e.key === "ArrowRight") {
        setImageViewerState((prev) => ({
          ...prev,
          currentIndex:
            prev.currentIndex === prev.images.length - 1
              ? 0
              : prev.currentIndex + 1,
        }));
      } else if (e.key === "Escape") {
        setImageViewerState((prev) => ({ ...prev, isOpen: false }));
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [imageViewerState.isOpen]);

  // If no scope exists yet, show empty state
  if (!scope) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <MessageSquare className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          No Scope Created Yet
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Create a scope of work after discussions and meetings are completed.
        </p>
        {scopeError ? (
          <p className="text-xs text-red-600 mb-2">{scopeError}</p>
        ) : null}
        {isLoadingScope ? (
          <p className="text-xs text-gray-400 mb-2">Loading scope...</p>
        ) : null}
        <p className="text-xs text-gray-400">
          Use the "Create Scope" button from the Lead View to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Image Carousel Modal */}
      {imageViewerState.isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4">
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
            onClick={() =>
              setImageViewerState((prev) => ({ ...prev, isOpen: false }))
            }
          >
            <X className="w-8 h-8" />
          </button>

          {imageViewerState.images.length > 1 && (
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors"
              onClick={() =>
                setImageViewerState((prev) => ({
                  ...prev,
                  currentIndex:
                    prev.currentIndex === 0
                      ? prev.images.length - 1
                      : prev.currentIndex - 1,
                }))
              }
            >
              <ChevronLeft className="w-10 h-10" />
            </button>
          )}

          <div className="max-w-5xl max-h-[85vh] w-full flex items-center justify-center">
            <img
              src={
                imageViewerState.images[imageViewerState.currentIndex]
                  ?.image_url ||
                imageViewerState.images[imageViewerState.currentIndex]?.image
              }
              alt="Requirement"
              className="max-w-full max-h-full object-contain"
            />
          </div>

          {imageViewerState.images.length > 1 && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors"
              onClick={() =>
                setImageViewerState((prev) => ({
                  ...prev,
                  currentIndex:
                    prev.currentIndex === prev.images.length - 1
                      ? 0
                      : prev.currentIndex + 1,
                }))
              }
            >
              <ChevronRight className="w-10 h-10" />
            </button>
          )}

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {imageViewerState.images.map((_, idx) => (
              <div
                key={idx}
                className={`w-2 h-2 rounded-full transition-colors ${idx === imageViewerState.currentIndex
                    ? "bg-white"
                    : "bg-white/30"
                  }`}
              />
            ))}
          </div>
        </div>
      )}
      {/* Header - Full Width */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-0.5">
            <span>{client.name}</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-900 font-medium">Scope of Work</span>
          </div>
          <h1 className="text-lg font-bold text-gray-900">{scope.name}</h1>
        </div>
        <div className="flex gap-1.5">
          <button
            type="button"
            className={`h-7 w-7 flex items-center justify-center border border-gray-300 rounded-md transition-colors ${isRefreshingView ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50"}`}
            onClick={handleRefreshView}
            title="Refresh"
            aria-label="Refresh"
            disabled={isRefreshingView}
          >
            <RefreshCw className={`w-3 h-3 ${isRefreshingView ? "animate-spin" : ""}`} />
          </button>
          {/* Non-approver workflow */}
          {!canApprove &&
            (statusRaw === "draft" || statusRaw === "rejected") ? (
            <button
              className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${isWorkingOnScopeAction
                  ? "opacity-60 cursor-not-allowed"
                  : "hover:bg-gray-50"
                }`}
              disabled={isWorkingOnScopeAction}
              onClick={sendForApproval}
            >
              <Send className="w-3 h-3" />
              {isWorkingOnScopeAction ? "Sending..." : "Send for Approval"}
            </button>
          ) : null}

          {canApprove && (statusRaw === "draft" || statusRaw === "rejected") ? (
            <button
              className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${isWorkingOnScopeAction
                  ? "opacity-60 cursor-not-allowed"
                  : "hover:bg-gray-50"
                }`}
              disabled={isWorkingOnScopeAction}
              onClick={selfApproveAndSendToClient}
            >
              <Send className="w-3 h-3" />
              {isWorkingOnScopeAction
                ? "Sending..."
                : "Approve & Send to client"}
            </button>
          ) : null}

          {/* Approver workflow */}
          {canApprove && statusRaw === "sent" ? (
            <>
              <button
                className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${isWorkingOnScopeAction
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:bg-gray-50"
                  }`}
                disabled={isWorkingOnScopeAction}
                onClick={approveAsApprover}
              >
                <Check className="w-3 h-3" />
                {isWorkingOnScopeAction ? "Working..." : "Approve Scope"}
              </button>
              <button
                type="button"
                className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-red-300 text-red-700 rounded-md transition-colors ${isWorkingOnScopeAction
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:bg-red-50"
                  }`}
                disabled={isWorkingOnScopeAction}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  rejectAsApprover();
                }}
              >
                Reject Scope
              </button>
            </>
          ) : null}

          {canApprove && statusRaw === "approver_approved" ? (
            <>
              <button
                className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${isWorkingOnScopeAction
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:bg-gray-50"
                  }`}
                disabled={isWorkingOnScopeAction}
                onClick={approveAsClient}
              >
                <Check className="w-3 h-3" />
                Client Approved
              </button>
              <button
                className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${isWorkingOnScopeAction
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:bg-gray-50"
                  }`}
                disabled={isWorkingOnScopeAction}
                onClick={openClientIssueModal}
              >
                <MessageSquare className="w-3 h-3" />
                Client Issue
              </button>
            </>
          ) : null}

          {canApprove && isClientApproved ? (
            <>
              <button
                className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium text-white rounded-md transition-colors ${isProceedingToQuotation || quotationExists ? "bg-gray-300 cursor-not-allowed" : "bg-sm-primary hover:bg-sm-hover"
                  }`}
                disabled={isProceedingToQuotation || quotationExists}
                onClick={proceedToQuotation}
              >
                <ArrowRight className="w-3 h-3" />
                {isProceedingToQuotation ? "Opening..." : quotationExists ? "Quotation Created" : "Proceed to Quotation"}
              </button>
            </>
          ) : null}
        </div>
      </div>

      {/* Full Width Scope Details */}
      <div className="flex-1 overflow-auto">
        <div className="bg-white rounded-md border border-gray-200 p-4">
          {scopeActionError ? (
            <div className="mb-3 px-3 py-2 rounded-md bg-red-50 border border-red-100 text-xs text-red-700">
              {scopeActionError}
            </div>
          ) : null}
          {/* Scope Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              {getStatusBadge(statusTagForHeader())}
              <span className="text-xs text-gray-500">
                Version {scope?.currentVersion ?? "-"}
              </span>
            </div>
            {canApprove && statusRaw === "client_approved" ? null : null}
          </div>

          {/* Project Metadata - Compact Grid */}
          <div className="grid grid-cols-5 gap-3 p-3 bg-gray-50 rounded-md mb-4">
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                Type
              </p>
              <p className="text-xs font-medium">{scope.projectType}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                Size
              </p>
              <p className="text-xs font-medium">{scope.propertySize}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                Budget
              </p>
              <p className="text-xs font-medium">{scope.budget}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                Start
              </p>
              <p className="text-xs font-medium">{scope.tentativeStart}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                Status
              </p>
              <p className="text-xs font-medium">{scope.status}</p>
            </div>
          </div>

          {/* Categories */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900">
                Requirements
              </h3>
              <button
                className={`flex items-center gap-1.5 h-7 px-3 text-xs font-medium border border-gray-300 rounded-md transition-colors ${canEditRequirements
                    ? "hover:bg-gray-50"
                    : "opacity-50 cursor-not-allowed"
                  }`}
                disabled={!canEditRequirements}
                onClick={() => {
                  if (!canEditRequirements) return;
                  setCategoryError("");
                  if (isAddingCategory) {
                    setNewCategories([...newCategories, { name: "" }]);
                  } else {
                    setIsAddingCategory(true);
                    setNewCategories([{ name: "" }]);
                  }
                }}
              >
                <Plus className="w-3 h-3" />
                Add Category
              </button>
            </div>

            {requirementError ? (
              <div className="mb-2 px-3 py-2 rounded-md bg-red-50 border border-red-100 text-xs text-red-700">
                {requirementError}
              </div>
            ) : null}

            <div className="space-y-1.5">
              {scope.categories.map((category) => (
                <div
                  key={category.id}
                  className="border border-gray-200 rounded overflow-visible"
                >
                  <div className="w-full flex items-center justify-between p-2 bg-gray-50">
                    <div className="flex items-center gap-1.5">
                      <ChevronDown className="w-3.5 h-3.5" />
                      <span className="text-sm font-medium">
                        {category.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className={`flex items-center gap-1 h-6 px-2 text-[10px] font-medium border border-gray-300 rounded-md transition-colors ${canEditRequirements
                            ? "hover:bg-gray-100 text-gray-700"
                            : "opacity-50 cursor-not-allowed text-gray-400"
                          }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!canEditRequirements) return;
                          setRequirementError("");
                          if (addingRequirementsFor === category.id) {
                            setNewRequirements([
                              ...newRequirements,
                              {
                                field: "",
                                type: "",
                                value: "",
                                imageFiles: [],
                              },
                            ]);
                          } else {
                            setAddingRequirementsFor(category.id);
                            setNewRequirements([
                              {
                                field: "",
                                type: "",
                                value: "",
                                imageFiles: [],
                              },
                            ]);
                          }
                        }}
                        disabled={!canEditRequirements}
                      >
                        <Plus className="w-3 h-3" />
                        Add Requirement
                      </button>
                      <span className="text-[10px] text-gray-500">
                        {(category.requirements || []).length} fields
                      </span>
                      <div className="relative">
                        <button
                          className={`h-6 w-6 flex items-center justify-center rounded-md transition-colors ${canEditRequirements
                              ? "hover:bg-gray-200"
                              : "opacity-50 cursor-not-allowed"
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!canEditRequirements) return;
                            setOpenMenu((prev) =>
                              prev?.type === "category" &&
                                prev?.id === String(category.id)
                                ? null
                                : { type: "category", id: String(category.id) },
                            );
                          }}
                          disabled={!canEditRequirements}
                        >
                          <MoreVertical className="w-3.5 h-3.5 text-gray-500" />
                        </button>
                        {openMenu?.type === "category" &&
                          openMenu?.id === String(category.id) ? (
                          <div className="absolute right-0 mt-1 w-28 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden z-10">
                            <button
                              className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenu(null);
                                openCategoryEdit(category);
                              }}
                            >
                              <Pencil className="w-3.5 h-3.5 text-gray-500" />
                              Edit
                            </button>
                            <button
                              className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2 text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenu(null);
                                deleteCategory(category.id);
                              }}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Delete
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-gray-200">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left text-[10px] font-medium text-gray-500 px-2.5 py-1.5 uppercase tracking-wide">
                            Field
                          </th>
                          <th className="text-left text-[10px] font-medium text-gray-500 px-2.5 py-1.5 uppercase tracking-wide">
                            Type
                          </th>
                          <th className="text-left text-[10px] font-medium text-gray-500 px-2.5 py-1.5 uppercase tracking-wide">
                            Value
                          </th>
                          <th className="text-center text-[10px] font-medium text-gray-500 px-2.5 py-1.5 uppercase tracking-wide w-12">
                            Photo
                          </th>
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(category.requirements || []).map((req) => (
                          <tr key={req.id} className="border-t border-gray-200">
                            <td className="px-2.5 py-1.5 text-xs">
                              {req.field}
                            </td>
                            <td className="px-2.5 py-1.5 text-xs text-gray-500">
                              {req.type}
                            </td>
                            <td className="px-2.5 py-1.5 text-xs">
                              {req.value}
                            </td>
                            <td className="px-2.5 py-1.5 text-center">
                              {req.images_count > 0 ||
                                (Array.isArray(req.images) &&
                                  req.images.length > 0) ? (
                                <button
                                  type="button"
                                  className="mx-auto flex items-center justify-center"
                                  onClick={() => openRequirementImage(req)}
                                >
                                  <ImageIcon className="w-3.5 h-3.5 text-sm-primary" />
                                </button>
                              ) : (
                                <span className="text-gray-400 text-xs">-</span>
                              )}
                            </td>
                            <td className="px-1">
                              <div className="flex items-center gap-1">
                                <button
                                  className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors"
                                  onClick={() =>
                                    openRequirementEdit(category.id, req)
                                  }
                                  title="Edit"
                                  disabled={!canEditRequirements}
                                >
                                  <Pencil className="w-3 h-3 text-gray-500" />
                                </button>
                                <button
                                  className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors"
                                  onClick={() => deleteRequirement(req.id)}
                                  title="Delete"
                                  disabled={!canEditRequirements}
                                >
                                  <Trash2 className="w-3 h-3 text-gray-400" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}

                        {addingRequirementsFor === category.id && (
                          <>
                            {newRequirements.map((nr, idx) => (
                              <tr
                                key={`new-req-${idx}`}
                                className="border-t border-gray-200 bg-white"
                              >
                                <td className="px-2.5 py-1.5">
                                  <input
                                    className="w-full h-7 px-2 text-[11px] border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-sm-primary bg-white"
                                    placeholder="Field Name"
                                    value={nr.field}
                                    onChange={(e) => {
                                      const list = [...newRequirements];
                                      list[idx].field = e.target.value;
                                      setNewRequirements(list);
                                    }}
                                    disabled={isCreatingRequirement}
                                  />
                                </td>
                                <td className="px-2.5 py-1.5">
                                  {/* <select
                                    className="w-full h-7 px-1 text-[11px] border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-sm-primary bg-white"
                                    value={nr.type}
                                    onChange={(e) => {
                                      const list = [...newRequirements];
                                      list[idx].type = e.target.value;
                                      setNewRequirements(list);
                                    }}
                                    disabled={isCreatingRequirement}
                                  >
                                    <option value="Text">Text</option> */}
                                  <select
                                    className="w-full h-7 px-1 text-[11px] border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-sm-primary bg-white"
                                    value={nr.type}
                                    onChange={(e) => {
                                      const list = [...newRequirements];
                                      const nextType = e.target.value;
                                      list[idx].type = nextType;
                                      if (nextType === "Color") {
                                        list[idx].value = normalizeColorValue(list[idx].value);
                                      } else if (nextType === "Boolean") {
                                        list[idx].value = normalizeBooleanValue(list[idx].value);
                                      } else if (nextType === "Dimensions") {
                                        list[idx].value = "";
                                      } else {
                                        list[idx].value = "";
                                      }
                                      setNewRequirements(list);
                                    }}
                                    disabled={isCreatingRequirement}
                                  >
                                    <option value="" disabled>
                                      Select type
                                    </option>
                                    <option value="Text">Text</option>
                                    <option value="Boolean">Boolean</option>
                                    <option value="Dimensions">
                                      Dimensions
                                    </option>
                                    <option value="Number">Number</option>
                                    <option value="Color">Color</option>
                                  </select>
                                </td>
                                <td className="px-2.5 py-1.5">
                                  {nr.type === "Boolean" && (
                                    <select
                                      className="w-full h-7 px-2 text-[11px] border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-sm-primary bg-white"
                                      value={normalizeBooleanValue(nr.value)}
                                      onChange={(e) => {
                                        const list = [...newRequirements];
                                        list[idx].value = e.target.value;
                                        setNewRequirements(list);
                                      }}
                                      disabled={isCreatingRequirement}
                                    >
                                      <option value="">Select</option>
                                      <option value="Yes">Yes</option>
                                      <option value="No">No</option>
                                    </select>
                                  )}
                                  {nr.type === "Color" && (
                                    <div className="flex items-center gap-1">
                                      <input
                                        type="color"
                                        className="h-7 w-7 border border-gray-300 rounded"
                                        value={normalizeColorValue(nr.value)}
                                        onChange={(e) => {
                                          const list = [...newRequirements];
                                          list[idx].value = e.target.value;
                                          setNewRequirements(list);
                                        }}
                                        disabled={isCreatingRequirement}
                                      />
                                      <input
                                        className="w-full h-7 px-2 text-[11px] border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-sm-primary bg-white font-mono"
                                        placeholder="#3B0069"
                                        value={nr.value || ""}
                                        onChange={(e) => {
                                          const list = [...newRequirements];
                                          list[idx].value = e.target.value;
                                          setNewRequirements(list);
                                        }}
                                        disabled={isCreatingRequirement}
                                      />
                                    </div>
                                  )}
                                  {nr.type === "Dimensions" && (
                                    <div className="flex flex-col gap-1">
                                      <div className="flex items-center gap-1">
                                        {(() => {
                                          const parts = parseDimensionValue(nr.value);
                                          return (
                                            <>
                                              <button
                                                type="button"
                                                className={`px-1.5 py-0.5 text-[10px] border rounded ${!parts.is3d
                                                  ? "bg-sm-primary text-white border-sm-primary"
                                                  : "bg-white text-gray-500 border-gray-300"
                                                  }`}
                                                onClick={() => {
                                                  const list = [...newRequirements];
                                                  const nextValue = buildDimensionValue(parts.a, parts.b, "", false);
                                                  list[idx].value = nextValue;
                                                  setNewRequirements(list);
                                                }}
                                                disabled={isCreatingRequirement}
                                              >
                                                2D
                                              </button>
                                              <button
                                                type="button"
                                                className={`px-1.5 py-0.5 text-[10px] border rounded ${parts.is3d
                                                  ? "bg-sm-primary text-white border-sm-primary"
                                                  : "bg-white text-gray-500 border-gray-300"
                                                  }`}
                                                onClick={() => {
                                                  const list = [...newRequirements];
                                                  const nextValue = buildDimensionValue(parts.a, parts.b, parts.c, true);
                                                  list[idx].value = nextValue;
                                                  setNewRequirements(list);
                                                }}
                                                disabled={isCreatingRequirement}
                                              >
                                                3D
                                              </button>
                                            </>
                                          );
                                        })()}
                                      </div>
                                      <div className="flex items-center gap-1">
                                        {(() => {
                                          const parts = parseDimensionValue(nr.value);
                                          return (
                                            <>
                                              <input
                                                className="w-12 h-7 px-1 text-[11px] border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-sm-primary bg-white"
                                                placeholder="0"
                                                value={parts.a}
                                                onChange={(e) => {
                                                  const list = [...newRequirements];
                                                  const next = buildDimensionValue(
                                                    e.target.value,
                                                    parts.b,
                                                    parts.c,
                                                    parts.is3d
                                                  );
                                                  list[idx].value = next;
                                                  setNewRequirements(list);
                                                }}
                                                disabled={isCreatingRequirement}
                                              />
                                              <span className="text-[10px] text-gray-400">x</span>
                                              <input
                                                className="w-12 h-7 px-1 text-[11px] border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-sm-primary bg-white"
                                                placeholder="0"
                                                value={parts.b}
                                                onChange={(e) => {
                                                  const list = [...newRequirements];
                                                  const next = buildDimensionValue(
                                                    parts.a,
                                                    e.target.value,
                                                    parts.c,
                                                    parts.is3d
                                                  );
                                                  list[idx].value = next;
                                                  setNewRequirements(list);
                                                }}
                                                disabled={isCreatingRequirement}
                                              />
                                              {parts.is3d && (
                                                <>
                                                  <span className="text-[10px] text-gray-400">x</span>
                                                  <input
                                                    className="w-12 h-7 px-1 text-[11px] border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-sm-primary bg-white"
                                                    placeholder="0"
                                                    value={parts.c}
                                                    onChange={(e) => {
                                                      const list = [...newRequirements];
                                                      const next = buildDimensionValue(
                                                        parts.a,
                                                        parts.b,
                                                        e.target.value,
                                                        parts.is3d
                                                      );
                                                      list[idx].value = next;
                                                      setNewRequirements(list);
                                                    }}
                                                    disabled={isCreatingRequirement}
                                                  />
                                                </>
                                              )}
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  )}
                                  {nr.type !== "Boolean" && nr.type !== "Color" && nr.type !== "Dimensions" && (
                                    <input
                                      className="w-full h-7 px-2 text-[11px] border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-sm-primary bg-white"
                                      placeholder="Value"
                                      value={nr.value}
                                      onChange={(e) => {
                                        const list = [...newRequirements];
                                        list[idx].value = e.target.value;
                                        setNewRequirements(list);
                                      }}
                                      disabled={isCreatingRequirement}
                                    />
                                  )}
                                </td>
                                <td className="px-2.5 py-1.5 text-center">
                                  <div className="flex flex-col items-center gap-1">
                                    <button
                                      type="button"
                                      className="p-1 rounded hover:bg-gray-100 transition-colors"
                                      onClick={() => {
                                        const input =
                                          document.createElement("input");
                                        input.type = "file";
                                        input.accept = "image/*";
                                        input.multiple = true;
                                        input.onchange = (e) => {
                                          const files = Array.from(
                                            e.target.files || [],
                                          );
                                          const list = [...newRequirements];
                                          list[idx].imageFiles = [
                                            ...(list[idx].imageFiles || []),
                                            ...files,
                                          ];
                                          setNewRequirements(list);
                                        };
                                        input.click();
                                      }}
                                    >
                                      <ImageIcon
                                        className={`w-4 h-4 ${nr.imageFiles?.length > 0
                                            ? "text-sm-primary"
                                            : "text-gray-400"
                                          }`}
                                      />
                                    </button>
                                    {nr.imageFiles?.length > 0 && (
                                      <span className="text-[9px] text-sm-primary font-medium">
                                        {nr.imageFiles.length}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-1 text-center">
                                  <button
                                    onClick={() =>
                                      setNewRequirements(
                                        newRequirements.filter(
                                          (_, i) => i !== idx,
                                        ),
                                      )
                                    }
                                    className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                                    disabled={isCreatingRequirement}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                            <tr className="bg-white border-t border-gray-200">
                              <td colSpan={5} className="px-2.5 py-2">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    className="px-2 py-1 text-[10px] font-medium border border-gray-300 rounded bg-white hover:bg-gray-50 transition-colors"
                                    onClick={() =>
                                      setAddingRequirementsFor(null)
                                    }
                                    disabled={isCreatingRequirement}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    className={`px-2 py-1 text-[10px] font-medium text-white rounded transition-colors ${isCreatingRequirement ||
                                        !newRequirements.some((r) =>
                                          r.field.trim(),
                                        )
                                        ? "bg-gray-300 cursor-not-allowed"
                                        : "bg-sm-primary hover:bg-sm-hover"
                                      }`}
                                    onClick={() =>
                                      handleCreateRequirements(category.id)
                                    }
                                    disabled={
                                      isCreatingRequirement ||
                                      !newRequirements.some((r) =>
                                        r.field.trim(),
                                      )
                                    }
                                  >
                                    {isCreatingRequirement
                                      ? "Saving..."
                                      : "Save Requirements"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              {isAddingCategory && (
                <div className="space-y-1.5 mt-2">
                  {newCategories.map((nc, idx) => (
                    <div
                      key={`new-cat-${idx}`}
                      className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded"
                    >
                      <input
                        className="flex-1 h-8 px-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-sm-primary bg-white"
                        placeholder="Category Name"
                        value={nc.name}
                        onChange={(e) => {
                          const list = [...newCategories];
                          list[idx].name = e.target.value;
                          setNewCategories(list);
                        }}
                        disabled={isCreatingCategory}
                      />
                      <button
                        onClick={() =>
                          setNewCategories(
                            newCategories.filter((_, i) => i !== idx),
                          )
                        }
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                        disabled={isCreatingCategory}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded">
                    {categoryError ? (
                      <span className="text-xs text-red-600">
                        {categoryError}
                      </span>
                    ) : (
                      <span />
                    )}
                    <div className="flex gap-2">
                      <button
                        className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded bg-white hover:bg-gray-50 transition-colors"
                        onClick={() => setIsAddingCategory(false)}
                        disabled={isCreatingCategory}
                      >
                        Cancel
                      </button>
                      <button
                        className={`px-3 py-1.5 text-xs font-medium text-white rounded transition-colors ${isCreatingCategory ||
                            !newCategories.some((c) => c.name.trim())
                            ? "bg-gray-300 cursor-not-allowed"
                            : "bg-sm-primary hover:bg-sm-hover"
                          }`}
                        onClick={handleCreateCategories}
                        disabled={
                          isCreatingCategory ||
                          !newCategories.some((c) => c.name.trim())
                        }
                      >
                        {isCreatingCategory
                          ? "Saving..."
                          : "Save All Categories"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Client Feedback - Compact */}
          <div className="mb-4 p-2.5 bg-sm-accent/10 border border-sm-accent/30 rounded">
            <div className="flex items-center gap-1.5 mb-1.5">
              <MessageSquare className="w-3.5 h-3.5 text-sm-accent" />
              <h4 className="text-xs font-medium text-sm-accent">
                Client Feedback
              </h4>
              <button
                className={`ml-auto h-6 px-2 flex items-center gap-1 text-[10px] font-medium border border-sm-accent/30 rounded transition-colors ${isClientApproved ? "opacity-50 cursor-not-allowed" : "hover:bg-sm-accent/10"}`}
                onClick={() => {
                  if (isClientApproved) return;
                  setFeedbackError("");
                  setNewFeedbackMessage("");
                  setIsClientIssueFlow(false);
                  setIsRejectScopeFlow(false);
                  setFeedbackCategory("approver");
                  setIsFeedbackModalOpen(true);
                }}
                disabled={isClientApproved}
              >
                <Edit3 className="w-3 h-3" />
                Add
              </button>
            </div>

            {feedbackError ? (
              <div className="text-[10px] text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1.5 mb-2">
                {feedbackError}
              </div>
            ) : null}

            {isLoadingFeedback ? (
              <div className="text-xs text-gray-500">Loading feedback...</div>
            ) : null}

            {!isLoadingFeedback && normalizedFeedback.length === 0 ? (
              <div className="text-xs text-gray-600">No feedback yet.</div>
            ) : null}

            {normalizedFeedback.map((fb) => (
              <div
                key={fb.id || fb.message}
                className="text-xs flex items-start justify-between gap-2"
              >
                <div className="min-w-0">
                  <p className="text-gray-900">{fb.message}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    {fb.date}
                    {fb.by ? ` - ${fb.by}` : ""}
                  </p>
                </div>
                <div className="flex items-start gap-1.5 shrink-0">
                  {fb.categoryLabel ? (
                    <span className="inline-flex items-center px-1.5 h-5 rounded border border-sm-accent/30 text-[10px] text-sm-accent bg-sm-accent/10 uppercase">
                      {fb.categoryLabel}
                    </span>
                  ) : null}
                  <div className="relative">
                    <button
                      className={`h-6 w-6 flex items-center justify-center rounded-md transition-colors ${isClientApproved ? "opacity-50 cursor-not-allowed" : "hover:bg-sm-accent/10"}`}
                      onClick={() => {
                        if (isClientApproved) return;
                        setOpenMenu((prev) =>
                          prev?.type === "feedback" && prev?.id === String(fb.id)
                            ? null
                            : { type: "feedback", id: String(fb.id) }
                        );
                      }}
                      disabled={isClientApproved}
                    >
                      <MoreVertical className="w-3.5 h-3.5 text-sm-accent" />
                    </button>
                    {openMenu?.type === "feedback" &&
                      openMenu?.id === String(fb.id) ? (
                      <div className="absolute right-0 mt-1 w-28 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden z-10">
                        <button
                          className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2"
                          onClick={() => {
                            setOpenMenu(null);
                            openFeedbackEdit(fb);
                          }}
                        >
                          <Pencil className="w-3.5 h-3.5 text-gray-500" />
                          Edit
                        </button>
                        <button
                          className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center gap-2 text-red-600"
                          onClick={() => {
                            setOpenMenu(null);
                            deleteFeedback(fb.id);
                          }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Edit Category Modal */}
          {editCategoryModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">
                    Edit Category
                  </h3>
                  <button
                    onClick={() => setEditCategoryModal(null)}
                    className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                    disabled={isSavingCategory}
                  >
                    ×
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-700">
                      Name *
                    </label>
                    <input
                      className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                      value={editCategoryName}
                      onChange={(e) => setEditCategoryName(e.target.value)}
                      disabled={isSavingCategory}
                    />
                  </div>
                </div>
                <div className="p-4 bg-gray-50 flex justify-end gap-2">
                  <button
                    className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                    onClick={() => setEditCategoryModal(null)}
                    disabled={isSavingCategory}
                  >
                    Cancel
                  </button>
                  <button
                    className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isSavingCategory || !editCategoryName.trim()
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-sm-primary hover:bg-sm-hover"
                      }`}
                    onClick={saveCategoryEdit}
                    disabled={isSavingCategory || !editCategoryName.trim()}
                  >
                    {isSavingCategory ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Requirement Modal */}
          {editRequirementModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">
                    Edit Requirement
                  </h3>
                  <button
                    onClick={() => {
                      setEditRequirementModal(null);
                      setConfirmDeleteReqImage(null);
                      setDeleteReqImageError("");
                    }}
                    className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                    disabled={isSavingRequirement}
                  >
                    ×
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-700">
                      Field *
                    </label>
                    <input
                      className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                      value={editRequirement.field}
                      onChange={(e) =>
                        setEditRequirement({
                          ...editRequirement,
                          field: e.target.value,
                        })
                      }
                      disabled={isSavingRequirement}
                    />
                  </div>
                  <div className="flex flex-col gap-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-700">
                        Type *
                      </label>
                      <select
                        className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary bg-white"
                        value={editRequirement.type}
                        onChange={(e) => {
                          const nextType = e.target.value;
                          let nextValue = editRequirement.value;
                          if (nextType === "Color") {
                            nextValue = normalizeColorValue(nextValue);
                          } else if (nextType === "Boolean") {
                            nextValue = normalizeBooleanValue(nextValue);
                          } else if (nextType === "Dimensions") {
                            nextValue = "";
                          } else if (nextType !== editRequirement.type) {
                            nextValue = "";
                          }
                          setEditRequirement({ ...editRequirement, type: nextType, value: nextValue });
                        }}
                        disabled={isSavingRequirement}
                      >
                        <option value="">Select type</option>
                        <option value="Boolean">Boolean</option>
                        <option value="Text">Text</option>
                        <option value="Dimensions">Dimensions</option>
                        <option value="Number">Number</option>
                        <option value="Color">Color</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-700">Value</label>
                      {editRequirement.type === "Boolean" && (
                        <select
                          className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary bg-white"
                          value={normalizeBooleanValue(editRequirement.value)}
                          onChange={(e) => setEditRequirement({ ...editRequirement, value: e.target.value })}
                          disabled={isSavingRequirement}
                        >
                          <option value="">Select</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      )}
                      {editRequirement.type === "Color" && (
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            className="h-8 w-8 border border-gray-300 rounded"
                            value={normalizeColorValue(editRequirement.value)}
                            onChange={(e) => setEditRequirement({ ...editRequirement, value: e.target.value })}
                            disabled={isSavingRequirement}
                          />
                          <input
                            className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary font-mono"
                            value={editRequirement.value || ""}
                            onChange={(e) => setEditRequirement({ ...editRequirement, value: e.target.value })}
                            disabled={isSavingRequirement}
                          />
                        </div>
                      )}
                      {editRequirement.type === "Dimensions" && (
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-1">
                            {(() => {
                              const parts = parseDimensionValue(editRequirement.value);
                              return (
                                <>
                                  <button
                                    type="button"
                                    className={`px-2 py-1 text-xs border rounded ${!parts.is3d
                                      ? "bg-sm-primary text-white border-sm-primary"
                                      : "bg-white text-gray-500 border-gray-300"
                                      }`}
                                    onClick={() =>
                                      setEditRequirement({
                                        ...editRequirement,
                                        value: buildDimensionValue(parts.a, parts.b, "", false),
                                      })
                                    }
                                    disabled={isSavingRequirement}
                                  >
                                    2D
                                  </button>
                                  <button
                                    type="button"
                                    className={`px-2 py-1 text-xs border rounded ${parts.is3d
                                      ? "bg-sm-primary text-white border-sm-primary"
                                      : "bg-white text-gray-500 border-gray-300"
                                      }`}
                                    onClick={() =>
                                      setEditRequirement({
                                        ...editRequirement,
                                        value: buildDimensionValue(parts.a, parts.b, parts.c, true),
                                      })
                                    }
                                    disabled={isSavingRequirement}
                                  >
                                    3D
                                  </button>
                                </>
                              );
                            })()}
                          </div>
                          <div className="flex items-center gap-2">
                            {(() => {
                              const parts = parseDimensionValue(editRequirement.value);
                              return (
                                <>
                                  <input
                                    className="flex-1 min-w-0 h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary bg-white"
                                    placeholder="0"
                                    value={parts.a}
                                    onChange={(e) =>
                                      setEditRequirement({
                                        ...editRequirement,
                                        value: buildDimensionValue(
                                          e.target.value,
                                          parts.b,
                                          parts.c,
                                          parts.is3d
                                        ),
                                      })
                                    }
                                    disabled={isSavingRequirement}
                                  />
                                  <span className="text-xs text-gray-400">x</span>
                                  <input
                                    className="flex-1 min-w-0 h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary bg-white"
                                    placeholder="0"
                                    value={parts.b}
                                    onChange={(e) =>
                                      setEditRequirement({
                                        ...editRequirement,
                                        value: buildDimensionValue(
                                          parts.a,
                                          e.target.value,
                                          parts.c,
                                          parts.is3d
                                        ),
                                      })
                                    }
                                    disabled={isSavingRequirement}
                                  />
                                  {parts.is3d && (
                                    <>
                                      <span className="text-xs text-gray-400">x</span>
                                      <input
                                        className="flex-1 min-w-0 h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary bg-white"
                                        placeholder="0"
                                        value={parts.c}
                                        onChange={(e) =>
                                          setEditRequirement({
                                            ...editRequirement,
                                            value: buildDimensionValue(
                                              parts.a,
                                              parts.b,
                                              e.target.value,
                                              parts.is3d
                                            ),
                                          })
                                        }
                                        disabled={isSavingRequirement}
                                      />
                                    </>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                      {editRequirement.type !== "Boolean" &&
                        editRequirement.type !== "Color" &&
                        editRequirement.type !== "Dimensions" && (
                          <input
                            className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary"
                            value={editRequirement.value}
                            onChange={(e) => setEditRequirement({ ...editRequirement, value: e.target.value })}
                            disabled={isSavingRequirement}
                          />
                        )}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-700 flex items-center justify-between">
                      <span>Images</span>
                      <button
                        type="button"
                        className="text-[10px] text-sm-primary font-medium hover:underline flex items-center gap-1"
                        onClick={() => {
                          const input = document.createElement("input");
                          input.type = "file";
                          input.accept = "image/*";
                          input.multiple = true;
                          input.onchange = (e) => {
                            const files = Array.from(e.target.files || []);
                            setEditRequirementNewFiles((prev) => [
                              ...prev,
                              ...files,
                            ]);
                          };
                          input.click();
                        }}
                      >
                        <Plus className="w-3 h-3" /> Add
                      </button>
                    </label>

                    {/* ── Existing saved images ── */}
                    {(editRequirementModal?.existingImages || []).length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {(editRequirementModal.existingImages).map((img, idx) => (
                          <div key={`existing-${idx}`} className="relative w-16 h-16 border border-gray-200 rounded overflow-visible">
                            <img
                              src={img.image_url || img.image}
                              alt="existing"
                              className="w-full h-full object-cover rounded cursor-pointer"
                              onClick={() =>
                                setImageViewerState({
                                  isOpen: true,
                                  images: editRequirementModal.existingImages,
                                  currentIndex: idx,
                                })
                              }
                            />
                            <button
                              type="button"
                              className={`absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 hover:bg-red-600
                       rounded-full flex items-center justify-center shadow-md
                       transition-colors z-10 ${isSavingRequirement || isDeletingReqImage ? "opacity-60 cursor-not-allowed" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isSavingRequirement || isDeletingReqImage) return;
                                confirmDeleteExistingRequirementImage(img);
                              }}
                              disabled={isSavingRequirement || isDeletingReqImage}
                            >
                              <X className="w-2.5 h-2.5 text-white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ── Newly added images (not yet saved) ── */}
                    {editRequirementNewFiles.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {editRequirementNewFiles.map((file, idx) => (
                          <div key={`new-${idx}`} className="relative w-16 h-16 border border-gray-200 rounded overflow-visible">
                            <img
                              src={URL.createObjectURL(file)}
                              alt="preview"
                              className="w-full h-full object-cover rounded"
                            />
                            <button
                              type="button"
                              className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 hover:bg-red-600
                       rounded-full flex items-center justify-center shadow-md
                       transition-colors z-10"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditRequirementNewFiles((prev) => prev.filter((_, i) => i !== idx));
                              }}
                            >
                              <X className="w-2.5 h-2.5 text-white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="p-4 bg-gray-50 flex justify-end gap-2">
                  <button
                    className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                    onClick={() => {
                      setEditRequirementModal(null);
                      setConfirmDeleteReqImage(null);
                      setDeleteReqImageError("");
                    }}
                    disabled={isSavingRequirement}
                  >
                    Cancel
                  </button>
                  <button
                    className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isSavingRequirement ||
                        !String(editRequirement.field || "").trim() ||
                        !String(editRequirement.type || "").trim()
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-sm-primary hover:bg-sm-hover"
                      }`}
                    onClick={saveRequirementEdit}
                    disabled={
                      isSavingRequirement ||
                      !String(editRequirement.field || "").trim() ||
                      !String(editRequirement.type || "").trim()
                    }
                  >
                    {isSavingRequirement ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Requirement Image Confirmation */}
          {confirmDeleteReqImage && (
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">Confirm Delete</h3>
                  <button
                    onClick={() => setConfirmDeleteReqImage(null)}
                    className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                    disabled={isDeletingReqImage}
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
                <div className="p-4 space-y-3 text-center">
                  {deleteReqImageError ? (
                    <div className="px-3 py-2 rounded-md bg-red-50 border border-red-100 text-xs text-red-700">
                      {deleteReqImageError}
                    </div>
                  ) : null}
                  <p className="text-sm text-gray-600 font-medium">Are you sure you want to delete this image?</p>
                  <p className="text-xs text-gray-500">This action cannot be undone.</p>
                </div>
                <div className="p-4 bg-gray-50 flex items-center justify-between">
                  <button
                    className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                    onClick={() => setConfirmDeleteReqImage(null)}
                    disabled={isDeletingReqImage}
                  >
                    Cancel
                  </button>
                  <button
                    className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isDeletingReqImage || confirmDeleteReqImage.imageId == null
                      ? "bg-red-300 cursor-not-allowed"
                      : "bg-red-600 hover:bg-red-700"
                      }`}
                    onClick={handleDeleteExistingRequirementImage}
                    disabled={isDeletingReqImage || confirmDeleteReqImage.imageId == null}
                  >
                    {isDeletingReqImage ? "Deleting..." : "Yes, delete"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Feedback Modal */}
          {editFeedbackModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">
                    Edit Feedback
                  </h3>
                  <button
                    onClick={() => setEditFeedbackModal(null)}
                    className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                    disabled={isSavingFeedback}
                  >
                    ×
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-700">
                      Message *
                    </label>
                    <textarea
                      className="w-full min-h-[90px] p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary resize-none"
                      value={editFeedbackMessage}
                      onChange={(e) => setEditFeedbackMessage(e.target.value)}
                      disabled={isSavingFeedback}
                    />
                  </div>
                </div>
                <div className="p-4 bg-gray-50 flex justify-end gap-2">
                  <button
                    className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                    onClick={() => setEditFeedbackModal(null)}
                    disabled={isSavingFeedback}
                  >
                    Cancel
                  </button>
                  <button
                    className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isSavingFeedback || !editFeedbackMessage.trim()
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-sm-primary hover:bg-sm-hover"
                      }`}
                    onClick={saveFeedbackEdit}
                    disabled={isSavingFeedback || !editFeedbackMessage.trim()}
                  >
                    {isSavingFeedback ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add Feedback Modal */}
          {isFeedbackModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">
                    {isRejectScopeFlow ? "Reject Scope Feedback" : "Add Feedback"}
                  </h3>
                  <button
                    onClick={closeFeedbackModal}
                    className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                    disabled={isCreatingFeedback}
                  >
                    ×
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-700">
                      Message *
                    </label>
                    <textarea
                      placeholder="The client requested to change..."
                      className="w-full min-h-[80px] p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sm-primary resize-none"
                      value={newFeedbackMessage}
                      onChange={(e) => setNewFeedbackMessage(e.target.value)}
                      disabled={isCreatingFeedback}
                    />
                  </div>
                </div>
                <div className="p-4 bg-gray-50 flex justify-end gap-2">
                  <button
                    className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                    onClick={closeFeedbackModal}
                    disabled={isCreatingFeedback}
                  >
                    Cancel
                  </button>
                  <button
                    className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${isCreatingFeedback || !newFeedbackMessage.trim()
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-sm-primary hover:bg-sm-hover"
                      }`}
                    onClick={handleCreateFeedback}
                    disabled={isCreatingFeedback || !newFeedbackMessage.trim()}
                  >
                    {isCreatingFeedback ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Version History */}
          <div className="mt-4">
            <button
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 transition-colors"
              onClick={async () => {
                const next = !showVersionHistory;
                setShowVersionHistory(next);
                if (next) await loadVersions();
              }}
            >
              <Clock className="w-3.5 h-3.5" />
              <span>Version History</span>
              {showVersionHistory ? (
                <ChevronDown className="w-3.5 h-3.5" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" />
              )}
            </button>
            {showVersionHistory && (
              <div className="mt-2 pl-4 border-l-2 border-gray-200 space-y-2">
                {versionsError ? (
                  <div className="text-xs text-red-600">{versionsError}</div>
                ) : null}
                {isLoadingVersions ? (
                  <div className="text-xs text-gray-500">
                    Loading versions...
                  </div>
                ) : null}
                {!isLoadingVersions &&
                  (Array.isArray(scopeVersions) ? scopeVersions : []).length ===
                  0 ? (
                  <div className="text-xs text-gray-500">No versions yet.</div>
                ) : null}
                {(Array.isArray(scopeVersions) ? scopeVersions : []).map(
                  (v) => (
                    <div key={v.id || v.version_number} className="relative">
                      <div className="absolute -left-[1.125rem] w-2 h-2 rounded-full bg-sm-primary border-2 border-white" />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-xs">
                            v{v.version_number ?? v.number ?? v.version ?? ""}
                          </span>
                          <span className="text-[10px] text-gray-500">
                            {formatDateOnly(
                              v.created_at ||
                              v.created_on ||
                              v.createdAt ||
                              v.date ||
                              "",
                            )}
                            {v.created_by ? ` • ${v.created_by}` : ""}
                          </span>
                          <button
                            className="ml-2 h-6 w-6 flex items-center justify-center rounded-md text-sm-primary hover:bg-sm-primary/10 transition-colors"
                            onClick={() => {
                              setSelectedVersionId(v.id);
                              setIsVersionModalOpen(true);
                              loadVersionDetail(v.id);
                            }}
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <p className="text-xs text-gray-500">
                          {v.changes_description ||
                            v.changes ||
                            v.message ||
                            ""}
                        </p>
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
            {isVersionModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-auto max-h-[90vh]">
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900">
                      Version Detail
                    </h3>
                    <button
                      onClick={() => setIsVersionModalOpen(false)}
                      className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                    >
                      ×
                    </button>
                  </div>
                  <div className="p-4 space-y-3 text-xs">
                    {isLoadingVersionDetail ? (
                      <p className="text-sm text-gray-500">Loading...</p>
                    ) : versionDetailError ? (
                      <p className="text-sm text-red-600">
                        {versionDetailError}
                      </p>
                    ) : (
                      <>
                        <p className="text-[10px] text-gray-500">
                          Snapshot recorded at{" "}
                          {formatDateOnly(
                            versionDetail?.created_at ||
                            versionDetail?.createdOn ||
                            "",
                          )}
                        </p>
                        <p className="text-[10px] text-gray-500">
                          Changes: {versionDetail?.changes_description || "—"}
                        </p>
                        <div className="space-y-3">
                          {versionSnapshotCategories.length === 0 ? (
                            <p className="text-sm text-gray-500">
                              No categories recorded in this version.
                            </p>
                          ) : (
                            versionSnapshotCategories.map((category) => {
                              const requirements = category.requirements || [];
                              return (
                                <div
                                  key={`version-cat-${category.id ?? category.name}`}
                                  className="rounded-lg border border-gray-200 bg-white/80 p-3 space-y-2"
                                >
                                  <div className="flex items-center justify-between">
                                    <p className="text-sm font-semibold text-gray-700">
                                      {category.name || "Untitled category"}
                                    </p>
                                    <span className="text-[10px] text-gray-500">
                                      {requirements.length} fields
                                    </span>
                                  </div>
                                  {requirements.length === 0 ? (
                                    <p className="text-[10px] text-gray-500">
                                      No fields recorded.
                                    </p>
                                  ) : (
                                    <div className="space-y-2">
                                      {requirements.map((req) => {
                                        const imagesCount =
                                          req.images_count ??
                                          (Array.isArray(req.images)
                                            ? req.images.length
                                            : null);
                                        return (
                                          <div
                                            key={`version-req-${req.id ?? req.field}`}
                                            className="border border-gray-100 rounded-md bg-gray-50 px-3 py-2"
                                          >
                                            <p className="text-[11px] font-semibold text-gray-700">
                                              {req.field || req.name || "Field"}
                                            </p>
                                            <div className="mt-1 flex flex-wrap gap-3 text-[10px] text-gray-500">
                                              <span>
                                                Type: {req.type || "—"}
                                              </span>
                                              <span>
                                                Value: {req.value ?? "—"}
                                              </span>
                                              {imagesCount > 0 ? (
                                                <span>
                                                  {imagesCount} image
                                                  {imagesCount === 1 ? "" : "s"}
                                                </span>
                                              ) : null}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
